import type { TipoPallet } from '../api';

/**
 * «Pegar desde Excel»: convierte lo copiado de la planilla de ZF en renglones
 * del formulario de Nuevo contenedor.
 *
 * La planilla la carga a mano el administrativo desde un Excel: en vez de
 * tipear renglón por renglón, selecciona las columnas en la hoja, copia
 * (Ctrl+C) y pega acá. Excel separa columnas con tab y filas con salto de
 * línea al copiar, que es el mismo formato que entiende este parser.
 */

export type ArticuloCatalogo = { codigo: string; descripcion: string; etiqueta: string };

/** El orden de columnas que espera el pegado. Se muestra como ejemplo en pantalla. */
export const COLUMNAS_PEGAR = ['Artículo', 'Piezas', 'Pallets', 'FIFO', 'Clase'] as const;

export type RenglonPegado = {
  /** El código resuelto, o lo tipeado tal cual si no se encontró. */
  articulo: string;
  articuloValido: boolean;
  numeroParte: string;
  piezas: string;
  piezasValidas: boolean;
  pallets: string;
  lote: string;
  tipo: TipoPallet;
};

/**
 * Busca el código del maestro a partir de lo tipeado: el código tal cual, el
 * «código · nombre» completo (lo que ofrece el buscador de artículo), o el
 * nombre si identifica un único artículo. El nombre se repite entre productos
 * distintos (el mismo material sale con varios códigos), así que un nombre
 * ambiguo no resuelve nada: mejor pedir el código que adivinar mal.
 */
export function resolverArticulo(texto: string, articulos: ArticuloCatalogo[]): string | null {
  const t = texto.trim().toUpperCase();
  if (!t) return null;
  const porCodigo = articulos.find((a) => a.codigo.toUpperCase() === t);
  if (porCodigo) return porCodigo.codigo;
  const porEtiqueta = articulos.find((a) => a.etiqueta.toUpperCase() === t);
  if (porEtiqueta) return porEtiqueta.codigo;
  const porNombre = articulos.filter((a) => a.descripcion.toUpperCase() === t);
  return porNombre.length === 1 ? porNombre[0]!.codigo : null;
}

const esEncabezado = (celdas: string[]) => /art[íi]culo/i.test(celdas[0] ?? '') || /pieza/i.test(celdas[1] ?? '');

const tipoDeTexto = (texto: string): TipoPallet => (/completo/i.test(texto) ? 'completo' : 'fraccionamiento');

/**
 * Parsea el texto pegado: una fila por renglón, columnas separadas por tab
 * (lo que arma Excel al copiar una selección). Una fila de encabezados al
 * principio —la típica «Artículo», «Piezas»…— se descarta sola. Las filas en
 * blanco no cuentan.
 *
 * No excluye nada: un artículo que no está en el maestro o unas piezas que no
 * son un número salen marcados (`articuloValido`/`piezasValidas` en falso)
 * para que la pantalla los muestre antes de sumarlos, no para perder la fila.
 */
export function parsearPegado(texto: string, articulos: ArticuloCatalogo[]): RenglonPegado[] {
  const filas = texto
    .split(/\r\n|\r|\n/)
    .map((fila) => fila.split('\t').map((celda) => celda.trim()))
    .filter((celdas) => celdas.some((celda) => celda !== ''));

  const [primera, ...resto] = filas;
  const cuerpo = primera && esEncabezado(primera) ? resto : filas;

  return cuerpo.map((celdas) => {
    const [articuloTxt = '', piezasTxt = '', palletsTxt = '', fifo = '', clase = ''] = celdas;
    const codigo = resolverArticulo(articuloTxt, articulos);
    const piezas = Math.trunc(Number(piezasTxt.replace(',', '.'))) || 0;
    return {
      articulo: codigo ?? articuloTxt.trim(),
      articuloValido: codigo !== null,
      numeroParte: codigo ?? '',
      piezas: piezasTxt.trim(),
      piezasValidas: piezasTxt.trim() !== '' && piezas > 0,
      pallets: palletsTxt.trim() || '1',
      lote: fifo.trim(),
      tipo: tipoDeTexto(clase),
    };
  });
}
